import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Loader2, KeyRound, Ban, Trash2, CheckCircle2, ShieldCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "partman", permissions: [] });
  const [saving, setSaving] = useState(false);
  const [reset, setReset] = useState(null);
  const [newPass, setNewPass] = useState("");

  const load = () => { setLoading(true); api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/meta/roles").then((r) => setRoles(r.data)); }, []);

  const create = async () => {
    if (!form.email || !form.password || !form.name) return toast.error("Lengkapi semua field");
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("User dibuat");
      setOpen(false); setForm({ email: "", password: "", name: "", role: "partman", permissions: [] }); load();
    } catch (err) { toast.error(apiError(err, "Gagal")); } finally { setSaving(false); }
  };

  const patch = async (id, body, msg) => {
    try { await api.patch(`/users/${id}`, body); toast.success(msg); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus user ini?")) return;
    try { await api.delete(`/users/${id}`); toast.success("User dihapus"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const doReset = async () => {
    if (newPass.length < 6) return toast.error("Min 6 karakter");
    await patch(reset.id, { password: newPass }, "Password direset");
    setReset(null); setNewPass("");
  };

  return (
    <div>
      <PageHeader title="User Management" subtitle="Buat & kelola akun. Role menentukan menu yang bisa diakses.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-gold text-white" data-testid="add-user-btn"><UserPlus size={16} className="mr-2" />Tambah User</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-head">Tambah User</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs uppercase text-gray-500">Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="user-name" /></div>
              <div><Label className="text-xs uppercase text-gray-500">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="user-email" /></div>
              <div><Label className="text-xs uppercase text-gray-500">Password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="user-password" /></div>
              <div>
                <Label className="text-xs uppercase text-gray-500">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.role === "partman" && (
                <label className="flex items-center gap-2 rounded-lg border border-gray-200 p-3 cursor-pointer" data-testid="perm-delete-spk-wrap">
                  <Checkbox
                    checked={form.permissions.includes("delete_spk")}
                    onCheckedChange={(c) => setForm({ ...form, permissions: c ? ["delete_spk"] : [] })}
                    data-testid="perm-delete-spk"
                  />
                  <span className="text-sm text-gray-700">Izinkan Delete / Void SPK</span>
                </label>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={create} disabled={saving} className="bg-gray-900" data-testid="save-user-btn">{saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {loading ? <Spinner /> : (
        <div className="grid gap-2">
          {users.map((u) => (
            <Card key={u.id} className="p-4 flex items-center justify-between gap-3" data-testid={`user-row-${u.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-gray-900 flex items-center gap-2">{u.name}{u.disabled && <span className="text-xs text-red-500">(disabled)</span>}</div>
                <div className="text-xs text-gray-400">{u.email} • {u.role_label}</div>
              </div>
              <div className="flex items-center gap-1">
                {u.role === "partman" && (
                  <Button size="icon" variant="ghost"
                    onClick={() => patch(u.id, { permissions: (u.permissions || []).includes("delete_spk") ? [] : ["delete_spk"] }, "Permission diperbarui")}
                    title={(u.permissions || []).includes("delete_spk") ? "Cabut izin Delete SPK" : "Beri izin Delete SPK"}
                    data-testid={`perm-${u.id}`}>
                    <ShieldCheck size={16} className={(u.permissions || []).includes("delete_spk") ? "text-emerald-500" : "text-gray-300"} />
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={() => setReset(u)} title="Reset Password" data-testid={`reset-${u.id}`}><KeyRound size={16} /></Button>
                <Button size="icon" variant="ghost" onClick={() => patch(u.id, { disabled: !u.disabled }, u.disabled ? "Diaktifkan" : "Dinonaktifkan")} title="Toggle Disable" data-testid={`toggle-${u.id}`}>
                  {u.disabled ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Ban size={16} />}
                </Button>
                {u.role !== "owner" && <Button size="icon" variant="ghost" onClick={() => del(u.id)} className="text-red-500" title="Hapus" data-testid={`delete-${u.id}`}><Trash2 size={16} /></Button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!reset} onOpenChange={(o) => !o && setReset(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Reset Password — {reset?.name}</DialogTitle></DialogHeader>
          <Input placeholder="Password baru" value={newPass} onChange={(e) => setNewPass(e.target.value)} data-testid="reset-password-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReset(null)}>Batal</Button>
            <Button onClick={doReset} className="bg-gray-900" data-testid="confirm-reset-btn">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
